/**
 * Auth state for the SPA: is a login required, are we logged in, and the login/logout
 * actions.
 *
 * The status query is the gate the whole app reads. It is refetched on window focus
 * (unlike the dashboard queries, which are not) so a session that expires while a tab
 * sits open is noticed the next time it is looked at. A 401 from any *other* query also
 * re-checks it, via the cache-level handler in main.tsx - so an expired session mid-poll
 * drops the app to the login screen without a manual reload.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api/client'
import type { AuthStatus } from '../api/types'

const AUTH_KEY = ['auth-status'] as const

export function useAuthStatus() {
  return useQuery({
    queryKey: AUTH_KEY,
    queryFn: api.authStatus,
    // The session's validity is the one thing worth re-checking on focus.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
    retry: false,
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      api.login(email, password),
    onSuccess: (status) => {
      queryClient.setQueryData(AUTH_KEY, status)
      if (status.authenticated) {
        // Fresh session - let the dashboard queries fetch now that they will pass.
        void queryClient.invalidateQueries()
      }
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.logout(),
    onSuccess: (status) => {
      queryClient.setQueryData(AUTH_KEY, status)
      // Drop every cached dashboard result, so nothing sensitive lingers after logout.
      queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth-status' })
    },
  })
}

/**
 * A single hook the app uses to decide what to render.
 *
 * `gated` is the actionable flag: true means "show the login screen". It is only true
 * once we know auth is required and we are not authenticated - never during the initial
 * status load, so the app does not flash the login form before the (possibly valid)
 * session is confirmed.
 */
export function useAuth() {
  const query = useAuthStatus()

  const status: AuthStatus | undefined = query.data
  const gated = Boolean(status?.auth_required && !status.authenticated)

  return {
    isLoading: query.isLoading,
    error: query.error,
    authRequired: Boolean(status?.auth_required),
    authenticated: Boolean(status?.authenticated),
    gated,
  }
}
